import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useArtifactKinds } from "@/api/queries";
import { Chip } from "@/components/ui/Chip";
import {
  ArrowRightIcon,
  ChartIcon,
  ChatIcon,
  CodeIcon,
  DashboardIcon,
  ReportIcon,
  ShieldIcon,
  SidebarIcon,
} from "@/components/icons";
import { useBrand } from "@/theme/context";
import { cn } from "@/lib/cn";

/**
 * The in-app user guide (PRD FR-12.2).
 *
 * Written for the analyst, not the deployer: asking questions, reading
 * artifacts, saving reports, and — the section that matters most — what the
 * safety guarantees actually are and how to check them yourself. Every claim
 * here points at the thing that proves it rather than asking to be believed.
 *
 * The product name and the assistant's name come from the theme, so a
 * rebranded deployment gets a guide about *its* product rather than about Ming.
 */

interface Section {
  id: string;
  title: string;
  icon: typeof ChatIcon;
  body: ReactNode;
}

function Term({ children }: { children: ReactNode }) {
  return <span className="text-ink font-medium">{children}</span>;
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mt-3 flex flex-col gap-2">
      {items.map((item, index) => (
        <li key={index} className="flex gap-3">
          <span className="bg-muted text-ink-secondary type-caption mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full tabular-nums">
            {index + 1}
          </span>
          <span className="type-body text-ink-secondary">{item}</span>
        </li>
      ))}
    </ol>
  );
}

export function GuidePage() {
  const { productName, assistantName } = useBrand();
  const { data: vocabulary } = useArtifactKinds();
  const [open, setOpen] = useState<string>("asking");

  const chartKinds = (vocabulary?.kinds ?? []).filter((kind) => kind.family);

  const sections: Section[] = [
    {
      id: "layout",
      title: "Finding your way around",
      icon: SidebarIcon,
      body: (
        <>
          <p className="type-body text-ink-secondary">
            Everything is in the left navigation: <Term>Workspace</Term> for conversations,
            reports, dashboards and the SQL workbench, <Term>Deployment</Term> for sources and
            schedules, then <Term>Admin</Term> and this guide. Fold a group shut, or fold the
            whole column down to an icon rail with <Term>⌘B</Term> (<Term>Ctrl+B</Term> on
            Windows). It stays that way on the next page.
          </p>
          <Steps
            items={[
              <>
                The foot of the navigation says what this deployment has — how many sources, how
                many verified — and which version it is running.
              </>,
              <>
                The <Term>⋮</Term> beside it opens <Term>Theme</Term>, <Term>API docs</Term> and{" "}
                <Term>Lock</Term>. Theme has three settings and the middle one,{" "}
                <Term>System</Term>, follows whatever your computer is set to; the menu stays
                open while you try them.
              </>,
              <>
                <Term>Lock</Term> ends your session and returns to the unlock page. It appears
                only if you unlocked with the access key.
              </>,
            ]}
          />
          <p className="type-body text-ink-secondary mt-4">
            On a phone the navigation is not available yet, and the strip across the top carries
            the same <Term>⋮</Term>.
          </p>
        </>
      ),
    },
    {
      id: "asking",
      title: "Asking questions",
      icon: ChatIcon,
      body: (
        <>
          <p className="type-body text-ink-secondary">
            Pick a data source, then ask in plain English. {assistantName} reads the schema
            before it writes any SQL, runs the query against the real database, and answers with
            the numbers it got back — never with numbers it remembered.
          </p>
          <Steps
            items={[
              <>
                Start with something broad — <Term>“what's in this database?”</Term> — if you do
                not know the source. The suggested questions on a new conversation are derived
                from the schema, not from a list somebody wrote.
              </>,
              <>
                Follow up in fragments. <Term>“break that down by region”</Term> keeps the metric,
                the date range and the filters from the previous answer and changes only the
                dimension you named. The chips under the composer show the scope being carried.
              </>,
              <>
                Say the period you mean. “This quarter” is resolved against the deployment's own
                clock and timezone, so it means the same thing at 09:00 and in a report that runs
                overnight.
              </>,
              <>
                Mention a table with <Term>@</Term> to pin it into scope when a name is ambiguous.
              </>,
              <>
                Ask across sources. <Term>Add source</Term> in the conversation header puts a
                second database in the conversation — sources an administrator has related to
                this one are offered first. {assistantName} queries each in its own dialect, then
                joins the results; the trace shows each step&rsquo;s source and the join as{" "}
                <Term>Joined q1 ⋈ q2</Term>, and the answer names the join key it used.
              </>,
            ]}
          />
          <p className="type-body text-ink-secondary mt-4">
            If an answer looks wrong, use <Term>Regenerate</Term> rather than rephrasing: both
            attempts are kept, which makes them comparable. The thumbs are worth pressing — they
            are the only signal that separates “this deployment works” from “nobody has said
            otherwise”.
          </p>
        </>
      ),
    },
    {
      id: "trust",
      title: "Checking an answer",
      icon: ShieldIcon,
      body: (
        <>
          <p className="type-body text-ink-secondary">
            Every answer carries the SQL that produced it. This is the part of {productName} worth
            learning first, because it turns “the model said so” into something you can verify in
            one click.
          </p>
          <Steps
            items={[
              <>
                The line under an answer — <Term>“4 tool calls · 2 queries · 1.8s”</Term> — expands
                into the timeline: every schema lookup and every statement, in order, with row
                counts and timings. It fills in live while the agent works.
              </>,
              <>
                Each statement is the exact text that ran. Copy it, or click a table name in it to
                open that relation in the schema browser.
              </>,
              <>
                <Term>Sources</Term> chips list the relations the SQL actually touched — resolved
                from the statement, not from the model's own account of itself.
              </>,
              <>
                <Term>Warnings</Term> chips are the platform speaking, not the model: a truncated
                result, an undocumented source, or a statement the guard refused.
              </>,
            ]}
          />
          <p className="type-body text-ink-secondary mt-4">
            The same rule applies to anything that <em>fails</em>. A save, rename, refresh or
            delete that does not go through says so — in a notice at the bottom of the window, or
            beside the form that asked for it — and the sentence it shows is the server's own
            reason, not a house apology. An action that appears to do nothing did nothing, and
            that is now something you can rely on.
          </p>
          <div className="border-border bg-surface rounded-container mt-4 border p-4">
            <p className="type-label text-ink">What cannot happen</p>
            <p className="type-body text-ink-secondary mt-2">
              {productName} is read-only by construction, not by instruction. Every statement is
              validated before the database sees it, the session itself is opened read-only, and
              results are capped and timed out. A write is refused even when the model is asked
              for one — by a question, or by notes an administrator wrote. Those refusals are
              recorded, and an administrator can see every one on the{" "}
              <Link to="/settings/audit" className="text-accent hover:underline">
                audit page
              </Link>
              .
            </p>
          </div>
        </>
      ),
    },
    {
      id: "artifacts",
      title: "Reading artifacts",
      icon: ChartIcon,
      body: (
        <>
          <p className="type-body text-ink-secondary">
            Answers come with cards, tables and charts, chosen by the shape of the result rather
            than by a setting. They render in the pane beside the conversation, and the pane
            follows whichever answer you are reading.
          </p>
          <Steps
            items={[
              <>
                <Term>Summary cards</Term> are headline numbers. <Term>Tables</Term> sort by
                clicking a column header. Charts have a swap menu for other kinds that suit the
                same data.
              </>,
              <>
                Numbers are formatted for reading — <Term>$18.4K</Term> — but exported raw. A CSV
                of that column contains <Term>18400.5</Term>.
              </>,
              <>
                A chart that could not honestly be drawn degrades to something that can, and says
                why on the artifact. A note explaining a degradation is never silent.
              </>,
            ]}
          />
          {chartKinds.length > 0 ? (
            <div className="mt-4">
              <p className="type-support text-ink-secondary mb-2">
                {chartKinds.length} chart kinds, in {new Set(chartKinds.map((k) => k.family)).size}{" "}
                families — a swap only offers the ones in the same family, because the others
                would misrepresent the data:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {chartKinds.map((kind) => (
                  <Chip key={kind.type} tone="neutral">
                    {kind.label}
                  </Chip>
                ))}
              </div>
            </div>
          ) : null}
        </>
      ),
    },
    {
      id: "reports",
      title: "Reports and dashboards",
      icon: ReportIcon,
      body: (
        <>
          <p className="type-body text-ink-secondary">
            A good answer becomes a <Term>report</Term>: the question, the narrative, and one
            tile per artifact — each carrying its own SQL and its own source. Reports compose into{" "}
            <Term>dashboards</Term>, which can mix sources.
          </p>
          <Steps
            items={[
              <>
                <Term>Save as report</Term> on any SQL-backed answer. The tiles render instantly,
                because the rows are already on the answer's timeline.
              </>,
              <>
                A tile is a <Term>copy</Term>. Deleting the conversation leaves the report
                standing, and swapping a report's chart does not touch the conversation's.
              </>,
              <>
                <Term>Edit SQL</Term> on a tile validates and runs the new statement before saving
                it, so a tile can never hold SQL that does not work.
              </>,
              <>
                <Term>Refresh All</Term> runs one query per distinct source-and-statement pair, so
                six tiles sharing three queries cost three.
              </>,
              <>
                A schedule refreshes a report or dashboard, snapshots it, renders a PDF and emails
                it. Snapshots also drive the <Term>trend</Term> view and{" "}
                <Term>Cause Lens</Term>, which explains what moved a number and shows the queries
                it used to work that out.
              </>,
            ]}
          />
        </>
      ),
    },
    {
      id: "workbench",
      title: "When you already know the query",
      icon: CodeIcon,
      body: (
        <>
          <p className="type-body text-ink-secondary">
            The{" "}
            <Link to="/workbench" className="text-accent hover:underline">
              SQL workbench
            </Link>{" "}
            runs statements you write yourself, through exactly the same guard, row cap and
            timeout as everything else. Results render as an artifact and can be pinned to a
            dashboard.
          </p>
          <p className="type-body text-ink-secondary mt-3">
            It is also the escape hatch when the model needs a nudge: run the query you want,
            confirm the shape, and put it on a dashboard directly.
          </p>
        </>
      ),
    },
    {
      id: "admin",
      title: "For whoever set this up",
      icon: DashboardIcon,
      body: (
        <>
          <Steps
            items={[
              <>
                <Link to="/sources" className="text-accent hover:underline">
                  Sources
                </Link>{" "}
                — add a database or upload a CSV, browse its schema, and write its{" "}
                <Term>data dictionary</Term>: the metric definitions, join rules and pitfalls the
                schema does not carry. On SQLite, which stores no comments at all, this is the
                only thing giving the schema meaning. <Term>Draft business memory</Term> writes a
                first version for you to review.
              </>,
              <>
                <Term>Health check</Term>, on a source's Health tab, is the acceptance test to run
                before handing the URL to anybody: it connects, reads the schema, grades the
                documentation, asks three real questions, and plants an injected write in the
                dictionary to prove the guard refuses it.
              </>,
              <>
                <Link to="/settings/providers" className="text-accent hover:underline">
                  Models
                </Link>{" "}
                — providers, discovery, and which model answers by default.{" "}
                <Link to="/settings/usage" className="text-accent hover:underline">
                  Usage
                </Link>{" "}
                is what it has cost.
              </>,
              <>
                <Link to="/settings/theming" className="text-accent hover:underline">
                  Branding
                </Link>{" "}
                — name, copy, logos and accent. It reaches the app, the unlock page, exported PDFs
                and scheduled emails at once.
              </>,
              <>
                <Link to="/settings/operations" className="text-accent hover:underline">
                  Operations
                </Link>{" "}
                — the limits this deployment enforces, what it keeps and for how long, and the
                training log with its export.
              </>,
            ]}
          />
        </>
      ),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-8">
        <p className="type-caption text-ink-disabled">Help</p>
        <h1 className="type-h1 text-ink mt-2">Using {productName}</h1>
        <p className="type-body text-ink-secondary mt-3">
          Ask questions in plain English; get answers backed by SQL you can read. This is the
          short version of everything worth knowing.
        </p>
      </header>

      <div className="flex flex-col gap-3">
        {sections.map((section) => {
          const Icon = section.icon;
          const expanded = open === section.id;
          return (
            <section
              key={section.id}
              className={cn(
                "rounded-container border",
                expanded ? "border-border-emphasized bg-surface" : "border-border bg-card",
              )}
            >
              <h2>
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? "" : section.id)}
                  className="motion-fast flex w-full items-center gap-3 px-4 py-3.5 text-start"
                >
                  <span className={cn(expanded ? "text-accent" : "text-ink-disabled")}>
                    <Icon className="size-5" />
                  </span>
                  <span className="type-h4 text-ink flex-1">{section.title}</span>
                  <ArrowRightIcon
                    className={cn(
                      "text-ink-disabled motion-fast size-4",
                      expanded && "rotate-90",
                    )}
                  />
                </button>
              </h2>
              {expanded ? <div className="px-4 pb-4 ps-12">{section.body}</div> : null}
            </section>
          );
        })}
      </div>

      <footer className="border-border mt-8 border-t pt-6">
        <p className="type-support text-ink-secondary">
          Every capability on these pages is also a documented API endpoint —{" "}
          <a href="/api/docs" target="_blank" rel="noreferrer" className="text-accent hover:underline">
            browse the API
          </a>{" "}
          if you would rather drive {productName} from a notebook.
        </p>
      </footer>
    </div>
  );
}
